import { useOutletContext, Link } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { ArrowUpRight, Code2, Layers, Rocket } from "lucide-react";
import { resolveUrl } from "@/lib/api";

export default function About() {
  const { profile } = useOutletContext();
  const pillars = [
    { icon: Code2, title: profile?.pillar1_title || "Frontend craft", text: profile?.pillar1_text || "Clean, accessible, performant React. I sweat the details: motion, focus states, and pixel rhythm." },
    { icon: Layers, title: profile?.pillar2_title || "Design systems", text: profile?.pillar2_text || "I build themeable, documented component libraries that scale across teams and brands." },
    { icon: Rocket, title: profile?.pillar3_title || "Full-stack growth", text: profile?.pillar3_text || "Actively deepening MERN and shipping automated CI/CD with GitHub Actions." },
  ];
  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-6 py-10" data-testid="about-page">
      <Seo title="About" description="A frontend developer who cares about how things feel — accessible, performant, and beautiful interfaces." path="/about" />
      <Reveal>
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">About</div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-light tracking-tighter mt-3 max-w-3xl leading-[1.05]">
          A frontend developer who cares about how things <span className="italic font-semibold">feel</span>.
        </h1>
      </Reveal>

      <div className="grid lg:grid-cols-12 gap-12 mt-16">
        <div className="lg:col-span-7">
          <Reveal>
            <p className="text-lg text-muted-foreground leading-relaxed whitespace-pre-line">
              {profile?.bio || "I'm a frontend-focused developer who loves turning complex problems into clean, delightful interfaces."}
            </p>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mt-10">
            {pillars.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.08}>
                <div className="glass rounded-2xl p-6 h-full">
                  <div className="h-11 w-11 grid place-items-center rounded-xl bg-primary text-primary-foreground">
                    <p.icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-heading text-lg mt-4">{p.title}</h3>
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
        <div className="lg:col-span-5">
          <Reveal delay={0.1}>
            <div className="glass rounded-3xl p-3 sticky top-28">
              <video
                src={resolveUrl(profile?.avatar_video || "/video/sandeepvarikuppala1.mov")}
                autoPlay
                loop
                muted
                playsInline
                className="w-full h-[360px] object-cover rounded-2xl"
              />
              <div className="p-5">
                <div className="font-heading text-xl">{profile?.name}</div>
                <div className="text-sm text-muted-foreground">{profile?.role}</div>
                <Link to="/contact" className="mt-5 inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-medium hover:opacity-90 transition-opacity">
                  Work with me <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  );
}
